import { OnboardingProgress } from "@/components/OnboardingProgress";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { CalendarPlus, Upload, Mail, Trash2, MapPin, ChevronDown, Check, X } from "lucide-react";

const EVENT_TYPES = [
  "Concerts & Live Entertainment",
  "Fairs, Festivals & Markets",
  "Academic & Co-ops",
  "Arts & Creative",
  "Sports & PE",
  "Field Trips & Excursions",
  "STEM & Robotics",
  "Social & Playdates",
  "Life Skills & Workshops",
  "Teen & Tween Specials",
  "Community Service",
  "Seasonal, Holiday & Camps",
];

// Setup page for Event Creators. Fields are locked to the exact sequence agreed with the
// product: Company Logo → Organizer / Business Name → Event Name → About Event → Event
// Photo → Location → Contact Number → Email → Social Links. Save creates a pending event
// carrying all 9 fields and signs the user out (they cannot enter any dashboard until an
// admin approves and Paystack payment lands).
const EMPTY = {
  company_logo: "",
  organizer_name: "",
  event_name: "",
  about_event: "",
  event_date: "",
  event_time: "",
  event_photo: "",
  location: "",
  contact_number: "",
  email: "",
  social_links: "",
  website: "",
};

export default function EventCreatorSetup() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const logoInputRef = useRef(null);
  const eventPhotoInputRef = useRef(null);
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    // Pre-fill from role_profile so users returning to edit see their prior organiser info.
    api.get("/role-profile")
      .then(({ data }) => {
        const p = data.profile || {};
        setForm((prev) => ({
          ...prev,
          company_logo: p.company_logo || "",
          organizer_name: p.organizer_name || p.business_name || "",
          location: p.location || "",
          contact_number: p.contact_number || p.phone || "",
          email: p.email || p.contact_email || user?.email || "",
          social_links: p.social_links || "",
          website: p.website || "",
        }));
      })
      .catch(() => {});
  }, [user]);

  const upload = async (file, kind, setField) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Image is too large (max 10MB)");
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/event-creator/upload?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setField(data.path);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setUploading("");
    }
  };

  const save = async () => {
    if (!form.organizer_name.trim()) return toast.error("Organizer / business name is required");
    if (!form.event_name.trim()) return toast.error("Please pick at least one type of event you host");
    setSaving(true);
    try {
      await api.post("/event-creator/submit-setup", form);
      const fresh = await refresh();
      if (fresh?.vendor_status === "approved" && fresh?.subscription_status === "active") {
        toast.success("Event submitted!");
        setSaving(false);
        return;
      }
      setSubmitted(true);
    } catch (e) {
      toast.error(errMsg(e, "Could not submit event"));
    } finally {
      setSaving(false);
    }
  };

  const finishSubmission = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    await logout();
    navigate("/login", { replace: true });
  };

  const ImageDropzone = ({ label, testid, value, onChange, hint, inputRef }) => (
    <div>
      <Label>{label}</Label>
      <div className="mt-2 flex items-center gap-4">
        {value ? (
          <div className="relative w-24 h-24 rounded-2xl overflow-hidden border border-sage/30 shrink-0">
            <img src={fileUrl(value)} alt={label} className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-1 right-1 bg-white/90 hover:bg-white p-1 rounded-full text-terracotta-dark"
              aria-label="Remove image"
              data-testid={`${testid}-remove-btn`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="w-24 h-24 rounded-2xl bg-softstone/60 border border-dashed border-sage/40 flex items-center justify-center text-forest/40 shrink-0">
            <Upload className="w-6 h-6" />
          </div>
        )}
        <div className="flex-1">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0], testid === "company-logo" ? "logo" : "event_photo", onChange)}
            data-testid={`${testid}-input`}
          />
          <Button
            type="button"
            variant="outline"
            className="rounded-full border-sage/40 text-forest"
            onClick={() => inputRef.current?.click()}
            disabled={uploading === (testid === "company-logo" ? "logo" : "event_photo")}
            data-testid={`${testid}-btn`}
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading === (testid === "company-logo" ? "logo" : "event_photo") ? "Uploading..." : (value ? "Replace image" : "Upload image")}
          </Button>
          <p className="text-xs text-forest/50 mt-1">{hint}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-10" data-testid="event-creator-setup">
        <OnboardingProgress />
      <Dialog open={submitted} onOpenChange={(open) => { if (!open) finishSubmission(); }}>
        <DialogContent className="max-w-md" data-testid="ec-submitted-dialog">
          <DialogHeader>
            <div className="mx-auto mb-3 inline-flex w-14 h-14 rounded-full bg-sage-light items-center justify-center">
              <Mail className="w-7 h-7 text-sage-dark" />
            </div>
            <DialogTitle className="text-center font-heading font-black text-forest text-xl">Profile submitted</DialogTitle>
            <DialogDescription className="text-center text-forest/70 pt-2" data-testid="ec-submitted-msg">
              Your profile has been submitted for approval. Please check your email for further updates and payment. Signing you out now…
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <div className="flex items-center gap-3 mb-6">
        <span className="w-11 h-11 rounded-full bg-sage flex items-center justify-center"><CalendarPlus className="w-5 h-5 text-white" /></span>
        <div>
          <h1 className="text-2xl font-black text-forest">Create Your Business Profile</h1>
          <p className="text-sm text-forest/60">Tell families about your Business</p>
        </div>
      </div>

      <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm space-y-5">
        <ImageDropzone
          label="Company Logo"
          testid="company-logo"
          value={form.company_logo}
          onChange={(v) => setForm((f) => ({ ...f, company_logo: v }))}
          hint="Square JPG or PNG, up to 10MB."
          inputRef={logoInputRef}
        />

        <div>
          <Label>Organizer / Business Name*</Label>
          <Input value={form.organizer_name} onChange={(e) => setForm({ ...form, organizer_name: e.target.value })}
            data-testid="ec-organizer-name" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="e.g. Little Explorers Co." />
        </div>

        <div>
          <Label>Type of Events you Host</Label>
          {(() => {
            const selected = (form.event_name || "")
              .split("|")
              .map((s) => s.trim())
              .filter(Boolean);
            const toggle = (opt) => {
              const next = selected.includes(opt)
                ? selected.filter((x) => x !== opt)
                : [...selected, opt];
              setForm({ ...form, event_name: next.join(" | ") });
            };
            return (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="mt-1 w-full min-h-[42px] flex items-center justify-between gap-2 rounded-xl bg-softstone/60 border border-sage/30 px-3 py-2 text-left hover:bg-softstone/80 transition-colors"
                    data-testid="ec-event-types-trigger"
                  >
                    <div className="flex flex-wrap gap-1.5 flex-1">
                      {selected.length === 0 ? (
                        <span className="text-sm text-forest/50">Select one or more event types</span>
                      ) : (
                        <span className="text-sm font-semibold text-forest" data-testid="ec-event-types-count">
                          {selected.length} selected
                        </span>
                      )}
                    </div>
                    <ChevronDown className="w-4 h-4 text-forest/50 shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-[min(26rem,calc(100vw-2rem))] rounded-2xl bg-cream border-sage/20 p-2 shadow-lg" data-testid="ec-event-types-panel">
                  <div className="flex items-center justify-between px-3 py-1">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50">Pick every type that applies</p>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, event_name: "" })}
                      disabled={selected.length === 0}
                      className="text-[11px] font-bold text-terracotta-dark hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
                      data-testid="ec-event-types-clear"
                    >
                      Clear list
                    </button>
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {EVENT_TYPES.map((opt) => {
                      const on = selected.includes(opt);
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => toggle(opt)}
                          className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-sm text-left transition-colors ${on ? "bg-sage-light text-sage-dark font-semibold" : "text-forest/80 hover:bg-softstone/70"}`}
                          data-testid={`ec-event-type-option-${opt}`}
                        >
                          <span>{opt}</span>
                          {on && <Check className="w-4 h-4 text-sage-dark shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            );
          })()}
        </div>

        <div>
          <Label>About your Business.</Label>
          <Textarea value={form.about_event} onChange={(e) => setForm({ ...form, about_event: e.target.value })}
            data-testid="ec-about-event" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" rows={4}
            placeholder="Tell us more about your business and the type of events you create." />
        </div>

        <div>
          <Label className="flex items-center gap-1"><MapPin className="w-4 h-4 text-sage-dark" /> Business Location</Label>
          <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
            data-testid="ec-location" className="mt-1 rounded-xl bg-softstone/60 border-sage/30"
            placeholder="e.g. Kirstenbosch Gardens, Cape Town" />
        </div>

        <div>
          <Label>Contact Number</Label>
          <Input value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })}
            data-testid="ec-contact-number" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="e.g. +27 82 000 0000" />
        </div>

        <div>
          <Label>Email Address</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="ec-email" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="you@yourdomain.co.za" />
        </div>

        <div>
          <Label>Social Links</Label>
          <Input value={form.social_links} onChange={(e) => setForm({ ...form, social_links: e.target.value })}
            data-testid="ec-social-links" className="mt-1 rounded-xl bg-softstone/60 border-sage/30"
            placeholder="Instagram, Facebook or website URL" />
        </div>

        <div>
          <Label>Website</Label>
          <Input type="url" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
            data-testid="ec-website" className="mt-1 rounded-xl bg-softstone/60 border-sage/30"
            placeholder="https://yourbusiness.co.za" />
        </div>

        <Button onClick={save} disabled={saving} className="w-full rounded-full bg-sage hover:bg-sage-dark text-white" data-testid="ec-save-btn">
          {saving ? "Submitting..." : "Save & submit for approval"}
        </Button>
      </div>
    </div>
  );
}
